"use client";

/**
 * Toast facade — dispatches to an in-module event bus so call sites
 * (`FgToast.success(...)`) work anywhere (event handlers, mutation callbacks,
 * top-level code) without needing a hook call.
 *
 * The actual `useToast()` hook lives inside `FgToastBus`, which MUST be
 * rendered inside `<ToastProvider>` so the context is available.
 */

import { useEffect, useCallback } from "react";
import { useToast as useSpecToast } from "@/components/ui/ToastProvider";

type ToastType = "success" | "error" | "info";

type Listener = (type: ToastType, message: string) => void;

const listeners = new Set<Listener>();

function emit(type: ToastType, message: string) {
  listeners.forEach((fn) => fn(type, message));
}

export const FgToast = {
  show(message: string, type: ToastType = "success"): void {
    emit(type, message);
  },
  success: (m: string) => emit("success", m),
  error: (m: string) => emit("error", m),
  info: (m: string) => emit("info", m),
};

/**
 * Renders inside `<ToastProvider>`. Subscribes to the FgToast event bus
 * and calls the real `useToast()` hook. Place `<FgToastBus />` once in the
 * app shell, or rely on `<ToastProvider>` to render it automatically.
 */
export function FgToastBus() {
  const { success, error, info } = useSpecToast();

  const handler = useCallback(
    (type: ToastType, message: string) => {
      if (type === "success") success(message);
      else if (type === "error") error(message);
      else info(message);
    },
    [success, error, info],
  );

  useEffect(() => {
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, [handler]);

  return null;
}
